import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { fmtUsd } from '../utils/fmtPrice';
import { withdrawUsdcFromHlWithBrowserWallet } from '../utils/hlWithdraw';
import { hlUsdcBalance, hlSpotUsdcBalance } from '../utils/hlMarketDisplay';
import './SingleAssetHlPortfolio.css';
import './HlFundUsdcPanel.css';
import './HlWithdrawModal.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

function availableFromBalances(b) {
  if (!b) return null;
  const tradable = Number(b.tradableUsdc ?? b.perpsUsdc ?? b.withdrawableUsdc);
  if (Number.isFinite(tradable)) return Math.max(0, tradable);
  return null;
}

/**
 * Withdraw USDC from Hyperliquid → Arbitrum (same linked wallet signs).
 */
export default function HlWithdrawModal({ open, onClose, walletAddress, onWithdrawn }) {
  const [balances, setBalances] = useState(null);
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingBal, setLoadingBal] = useState(false);

  const loadBalances = useCallback(async () => {
    if (!walletAddress) return;
    setLoadingBal(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_BASE}/hyperliquid/hl-usdc-balance`, {
        headers: authHeaders(),
        params: { address: walletAddress },
      });
      setBalances(data);
      const avail = availableFromBalances(data);
      if (avail != null && avail >= 1) {
        setAmount((prev) => (prev ? prev : avail.toFixed(2)));
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not load balance');
    } finally {
      setLoadingBal(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (!open) return undefined;
    setSuccess('');
    setError('');
    setDestination(walletAddress || '');
    setAmount('');
    loadBalances();
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, walletAddress, loadBalances, onClose, busy]);

  if (!open || !walletAddress) return null;

  const available = availableFromBalances(balances);
  const spot = hlSpotUsdcBalance(balances);
  const perps = hlUsdcBalance(balances);

  const setMax = () => {
    if (available != null && available >= 1) setAmount(available.toFixed(2));
  };

  const submit = async () => {
    setError('');
    setSuccess('');
    const n = Number(amount);
    if (!(n >= 1)) {
      setError('Enter at least $1.00 USDC (Hyperliquid minimum).');
      return;
    }
    if (available != null && n > available + 0.001) {
      setError(`Only ${fmtUsd(available)} is free to withdraw.`);
      return;
    }
    setBusy(true);
    try {
      const result = await withdrawUsdcFromHlWithBrowserWallet({
        walletAddress,
        amountUsd: n,
        destination: destination || walletAddress,
        apiBase: API_BASE,
        authHeaders,
      });
      const note =
        result.verifyNote ||
        'USDC usually arrives on Arbitrum within a few minutes. Check MetaMask on Arbitrum One.';
      setSuccess(
        `Withdrawal submitted for ${fmtUsd(result.amountUsd)}. ${note}`
      );
      setBalances(result.balances || balances);
      onWithdrawn?.(result);
      await loadBalances();
    } catch (e) {
      setError(e.message || 'Withdrawal failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sahl-portfolio-overlay" role="presentation" onClick={() => !busy && onClose?.()}>
      <div
        className="sahl-portfolio-dialog hl-fund-modal-dialog hl-withdraw-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hl-withdraw-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 id="hl-withdraw-modal-title">Withdraw from Hyperliquid</h2>
          <button
            type="button"
            className="sahl-portfolio-close"
            onClick={() => !busy && onClose?.()}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <p className="sahl-portfolio-hint">
          Sends free USDC from your Hyperliquid account to <strong>Arbitrum</strong> (native USDC). You
          sign in your wallet — Omni&apos;s trading agent <strong>cannot</strong> withdraw for you.
        </p>

        <div className="hl-withdraw-wallet">
          <span className="label">Linked wallet</span>
          <code>
            {walletAddress.slice(0, 8)}…{walletAddress.slice(-6)}
          </code>
        </div>

        <div className="hl-withdraw-balances">
          {loadingBal ? (
            <p className="sahl-muted">Loading balances…</p>
          ) : (
            <>
              <div>
                <span className="label">Free to withdraw</span>
                <strong>{available != null ? fmtUsd(available) : '—'}</strong>
              </div>
              <div>
                <span className="label">Perps / tradable</span>
                <strong>{perps != null ? fmtUsd(perps) : '—'}</strong>
              </div>
              <div>
                <span className="label">Spot</span>
                <strong>{spot != null ? fmtUsd(spot) : '—'}</strong>
              </div>
            </>
          )}
        </div>

        {!balances?.unifiedAccount && Number(spot) > 0.01 && Number(available) < 1 && (
          <p className="hl-withdraw-warn">
            Spot has {fmtUsd(spot)} but little is free on perps. Use <strong>Move to trading</strong> on
            the trade panel first, then withdraw.
          </p>
        )}

        <div className="hl-withdraw-form">
          <label htmlFor="hl-withdraw-amount">Amount (USDC)</label>
          <div className="hl-withdraw-amount-row">
            <input
              id="hl-withdraw-amount"
              type="number"
              min={1}
              step="0.01"
              value={amount}
              disabled={busy}
              onChange={(e) => setAmount(e.target.value)}
            />
            <button type="button" className="hl-withdraw-max" disabled={busy || available == null} onClick={setMax}>
              Max
            </button>
          </div>

          <label htmlFor="hl-withdraw-dest">Destination (Arbitrum)</label>
          <input
            id="hl-withdraw-dest"
            type="text"
            value={destination}
            disabled={busy}
            onChange={(e) => setDestination(e.target.value.trim())}
            placeholder="0x…"
            spellCheck={false}
          />
          <p className="hl-withdraw-dest-hint">
            Defaults to your linked wallet. Change only if you intentionally want another Arbitrum address.
          </p>
        </div>

        {error && <p className="hl-withdraw-error">{error}</p>}
        {success && <p className="hl-withdraw-success">{success}</p>}

        <div className="hl-withdraw-actions">
          <button type="button" className="secondary-btn" disabled={busy} onClick={() => onClose?.()}>
            Cancel
          </button>
          <button
            type="button"
            className="hl-withdraw-submit"
            disabled={busy || !(Number(amount) >= 1)}
            onClick={submit}
          >
            {busy ? 'Confirm in wallet…' : 'Withdraw USDC'}
          </button>
        </div>
      </div>
    </div>
  );
}
